import {
  AlertOctagon,
  AlertTriangle,
  Ban,
  CheckCircle,
  Clock,
  CircleDot,
  Hourglass,
  PauseCircle,
  PlayCircle,
  ShieldCheck,
  Sparkles,
  Truck,
  Wrench,
} from 'lucide-react';
import type {
  InterventionPriorite,
  InterventionStatut,
  InterventionTypeDemande,
  InterventionTypeValidation,
  InterventionStatutValidation,
  InterventionEtatFinal,
} from '@mygmao/shared';

/**
 * Badges réutilisables pour les interventions (Phase 11).
 *
 * Mapping (cf. design-system/status-colors.md adapté pour US-INT-*) :
 *  Priorités :
 *   - P0 → rouge foncé (critique vital)
 *   - P1 → rouge (urgent)
 *   - P2 → orange (élevé)
 *   - P3 → amber (normal)
 *   - P4 → bleu (standard)
 *   - P5/6/7 → gris (basse)
 *
 *  Statuts :
 *   - nouveau → bleu
 *   - en_attente_validation → amber
 *   - affecte → indigo
 *   - planifie → cyan
 *   - en_cours → teal
 *   - en_attente_pieces → orange
 *   - en_attente_validation_finale → purple
 *   - cloture → vert
 *   - annule → gris
 */

const PRIORITE_LABELS: Record<InterventionPriorite, string> = {
  P0: 'P0 — Critique',
  P1: 'P1 — Urgent',
  P2: 'P2 — Élevé',
  P3: 'P3 — Normal',
  P4: 'P4 — Standard',
  P5: 'P5 — Basse',
  P6: 'P6 — Basse',
  P7: 'P7 — Basse',
};

const PRIORITE_CLASSES: Record<InterventionPriorite, string> = {
  P0: 'bg-red-200 text-red-900 border-red-300',
  P1: 'bg-red-100 text-red-800 border-red-200',
  P2: 'bg-orange-100 text-orange-800 border-orange-200',
  P3: 'bg-amber-100 text-amber-800 border-amber-200',
  P4: 'bg-blue-100 text-blue-800 border-blue-200',
  P5: 'bg-gray-100 text-gray-700 border-gray-200',
  P6: 'bg-gray-100 text-gray-700 border-gray-200',
  P7: 'bg-gray-100 text-gray-700 border-gray-200',
};

export function PrioriteBadge({
  priorite,
  compact,
}: {
  priorite: InterventionPriorite;
  compact?: boolean;
}) {
  const cls = PRIORITE_CLASSES[priorite];
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${cls}`}
    >
      {(priorite === 'P0' || priorite === 'P1') && <AlertOctagon className="w-3 h-3 mr-1" />}
      {compact ? priorite : PRIORITE_LABELS[priorite]}
    </span>
  );
}

const STATUT_CFG: Record<
  InterventionStatut,
  { cls: string; label: string; Icon: typeof CheckCircle }
> = {
  nouveau: {
    cls: 'bg-blue-100 text-blue-800 border-blue-200',
    label: 'Nouveau',
    Icon: Sparkles,
  },
  en_attente_validation: {
    cls: 'bg-amber-100 text-amber-800 border-amber-200',
    label: 'En attente validation',
    Icon: Hourglass,
  },
  affecte: {
    cls: 'bg-indigo-100 text-indigo-800 border-indigo-200',
    label: 'Affectée',
    Icon: CircleDot,
  },
  planifie: {
    cls: 'bg-cyan-100 text-cyan-800 border-cyan-200',
    label: 'Planifiée',
    Icon: Clock,
  },
  en_cours: {
    cls: 'bg-teal-100 text-teal-800 border-teal-200',
    label: 'En cours',
    Icon: PlayCircle,
  },
  en_attente_pieces: {
    cls: 'bg-orange-100 text-orange-800 border-orange-200',
    label: 'En attente pièces',
    Icon: PauseCircle,
  },
  en_attente_validation_finale: {
    cls: 'bg-purple-100 text-purple-800 border-purple-200',
    label: 'En attente validation finale',
    Icon: ShieldCheck,
  },
  cloture: {
    cls: 'bg-green-100 text-green-800 border-green-200',
    label: 'Clôturée',
    Icon: CheckCircle,
  },
  annule: {
    cls: 'bg-gray-100 text-gray-700 border-gray-200',
    label: 'Annulée',
    Icon: Ban,
  },
};

export function StatutInterventionBadge({ statut }: { statut: InterventionStatut }) {
  const c = STATUT_CFG[statut];
  if (!c) {
    return (
      <span className="inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border bg-gray-100 text-gray-700 border-gray-200">
        {statut}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${c.cls}`}
    >
      <c.Icon className="w-3 h-3 mr-1" />
      {c.label}
    </span>
  );
}

export function statutLabel(statut: InterventionStatut): string {
  return STATUT_CFG[statut]?.label ?? statut;
}

const TYPE_DEMANDE_LABELS: Record<InterventionTypeDemande, string> = {
  panne: 'Panne',
  urgence_critique: 'Urgence critique',
  preventive: 'Préventive',
  amelioration: 'Amélioration',
  installation: 'Installation',
  demenagement: 'Déménagement',
  controle_reglementaire: 'Contrôle réglementaire',
  expertise: 'Expertise',
};

export function typeDemandeLabel(type: InterventionTypeDemande): string {
  return TYPE_DEMANDE_LABELS[type] ?? type;
}

export function TypeDemandeBadge({ type }: { type: InterventionTypeDemande }) {
  const Icon =
    type === 'urgence_critique'
      ? AlertTriangle
      : type === 'preventive'
        ? Wrench
        : type === 'installation' || type === 'demenagement'
          ? Truck
          : CircleDot;
  return (
    <span className="inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border bg-gray-50 text-gray-700 border-gray-200">
      <Icon className="w-3 h-3 mr-1" />
      {TYPE_DEMANDE_LABELS[type]}
    </span>
  );
}

const TYPE_VALIDATION_LABELS: Record<InterventionTypeValidation, string> = {
  technique: 'Technique',
  budgetaire: 'Budgétaire',
  service: 'Service',
  securite: 'Sécurité',
  qualite: 'Qualité',
  direction: 'Direction',
};

export function typeValidationLabel(type: InterventionTypeValidation): string {
  return TYPE_VALIDATION_LABELS[type] ?? type;
}

const STATUT_VALIDATION_CFG: Record<InterventionStatutValidation, { cls: string; label: string }> =
  {
    en_attente: {
      cls: 'bg-amber-100 text-amber-800 border-amber-200',
      label: 'En attente',
    },
    approuve: {
      cls: 'bg-green-100 text-green-800 border-green-200',
      label: 'Approuvée',
    },
    rejete: { cls: 'bg-red-100 text-red-800 border-red-200', label: 'Rejetée' },
  };

export function StatutValidationBadge({ statut }: { statut: InterventionStatutValidation }) {
  const c = STATUT_VALIDATION_CFG[statut];
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${c.cls}`}
    >
      {c.label}
    </span>
  );
}

const ETAT_FINAL_LABELS: Record<InterventionEtatFinal, string> = {
  fonctionnel: 'Fonctionnel',
  degrade: 'Dégradé',
  hors_service: 'Hors service',
};

export function etatFinalLabel(etat: InterventionEtatFinal): string {
  return ETAT_FINAL_LABELS[etat] ?? etat;
}

export function EtatFinalBadge({ etat }: { etat: InterventionEtatFinal }) {
  const cls =
    etat === 'fonctionnel'
      ? 'bg-green-100 text-green-800 border-green-200'
      : etat === 'degrade'
        ? 'bg-amber-100 text-amber-800 border-amber-200'
        : 'bg-red-100 text-red-800 border-red-200';
  return (
    <span
      className={`inline-flex items-center text-xs px-1.5 py-0.5 rounded font-medium border ${cls}`}
    >
      {ETAT_FINAL_LABELS[etat]}
    </span>
  );
}

/** Convertit un nombre de minutes en "Xh Ym" / "Ymin" lisible. */
export function formatDuration(minutes: number | null | undefined): string {
  if (minutes === null || minutes === undefined) return '—';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}min`;
}
